// components/ui/TelefonLink.tsx
// Eine Telefonnummer als Wähl-Link. Rückfall IMMER `tel:` (Handy wählt, Desktop
// übergibt an die Webex-App) — das bleibt der einzige Weg für nicht verbundene
// Konten, Offline-Fälle und fehlgeschlagene Wähl-Versuche. Ist das Webex-Konto
// verbunden und die Nummer normalisierbar, wählt stattdessen die Telefonanlage
// (telefonie-dial): Webex ruft dabei zuerst das EIGENE Endgerät an, danach erst
// das Ziel — ohne Ansage hält man den Knopf sonst für kaputt.
import React from 'react';
import { telHref, normalisiereNummer } from '../../lib/telefonie/nummer';
import { istTelefonieVerbunden } from '../../lib/telefonie/status';
import { supabase } from '../../lib/supabase/client';
import { useNotification } from '../../contexts/NotificationContext';

export interface TelefonLinkProps {
  /** Rohnummer aus der Datenbank, in beliebigem Format. */
  nummer: string | null | undefined;
  /** Was angezeigt wird. Ohne Angabe die Rohnummer. */
  children?: React.ReactNode;
  className?: string;
  /** Für Karten: verhindert, dass der Klick die Karte mitöffnet. */
  stopPropagation?: boolean;
  /** Anzeige, wenn keine wählbare Nummer vorliegt. Ohne Angabe: children bzw. „–". */
  fallback?: React.ReactNode;
}

export default function TelefonLink({
  nummer, children, className, stopPropagation, fallback,
}: TelefonLinkProps) {
  const { showToast } = useNotification();
  const href = telHref(nummer);
  const inhalt = children ?? nummer ?? '–';

  // Async nach dem Wähl-Versuch fragen, aber preventDefault() MUSS synchron im
  // Klick-Handler fallen — nach einem await hat der Browser die Standardnavigation
  // (den tel:-Link) längst gestartet. Deshalb: sofort preventDefault, sobald die
  // Nummer normalisierbar ist, und bei Nicht-Verbunden/Fehler manuell auf href
  // zurückfallen (window.location.href), statt der Standardnavigation zu vertrauen.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (stopPropagation) e.stopPropagation();

    const e164 = normalisiereNummer(nummer);
    if (!e164) return; // nicht normalisierbar → tel:-Link greift wie bisher

    e.preventDefault();
    waehleUeberAnlage(e164, href, showToast);
  };

  if (!href) return <span className={className}>{fallback ?? inhalt}</span>;
  return (
    <a href={href} className={className} onClick={handleClick}>
      {inhalt}
    </a>
  );
}

async function waehleUeberAnlage(
  e164: string,
  href: string | null,
  showToast: ReturnType<typeof useNotification>['showToast'],
): Promise<void> {
  // Kein .catch() hier: istTelefonieVerbunden() fängt intern schon alles ab und
  // lehnt nie ab (siehe lib/telefonie/status.ts).
  const verbunden = await istTelefonieVerbunden();
  if (!verbunden) {
    if (href) window.location.href = href; // Rückfall: Konto nicht verbunden
    return;
  }

  try {
    const { error } = await supabase.functions.invoke('telefonie-dial', { body: { nummer: e164 } });
    if (error) throw error;
    showToast({
      type: 'info',
      // „Dein Telefon" war irreführend: bei uns ist das Endgerät die Webex-App auf dem
      // Rechner (in allen bisherigen Anrufen steht `endgeraet: APPLICATION`), nicht ein
      // Apparat. Wer auf ein Tischtelefon wartet, wartet vergebens — der User ist am
      // 2026-08-13 genau darüber gestolpert.
      message: 'Webex klingelt gleich — dort annehmen, dann wird gewählt.',
      modul: 'system',
    });
  } catch {
    showToast({
      type: 'error',
      message: 'Wählen über die Telefonanlage fehlgeschlagen. Anruf wird normal aufgebaut.',
      modul: 'system',
    });
    if (href) window.location.href = href; // Rückfall: Aufruf fehlgeschlagen
  }
}
